import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import { createColumnHelper } from '@tanstack/react-table';
import { commonMessages, generalMessages } from 'src/utils/messages';
import { useParams } from 'react-router-dom';
import { BILLING_STATUS_BADGE_OPTIONS } from 'src/features/servicePlanBillingPolicies/utils/constants';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { ServicePlanItemPricingPolicy } from 'src/features/servicePlanPricingPolicies/utils/types';
import { useListServicePlanPricingPolicies } from 'src/features/servicePlanPricingPolicies/api/useListServicePlanPricingPolicies';
import { pricingPoliciesMessages } from 'src/features/servicePlanPricingPolicies/utils/messages';
import {
    PAYMENT_TYPE_BADGE_OPTIONS,
    PRICING_MODEL_BADGE_OPTIONS,
} from 'src/features/servicePlanPricingPolicies/utils/constants';
import CreateServicePlanPricingPolicyFormModal from 'src/features/servicePlanPricingPolicies/components/forms/CreateServicePlanPricingPolicyFormModal';
import { CellCurrencyBadge } from 'src/features/moneyAccounts/components/tables/CellCurrencyBadge';
import CellServicePlanPricingPolicyActions from 'src/features/servicePlanPricingPolicies/components/tables/CellServicePlanPricingPolicyActions';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import React from 'react';
import useFilters from 'src/registry/hooks/useFilters';
import CellDate from 'src/registry/components/table/cells/CellDate';
import useSorting from 'src/registry/hooks/useSorting';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { BILLING_STATUS } from 'src/features/servicePlanBillingPolicies/utils/types';

type ServicePlanPricingPoliciesTableProps = {
    isServicePlanItemActive?: boolean;
};

const ServicePlanPricingPoliciesTable = ({ isServicePlanItemActive }: ServicePlanPricingPoliciesTableProps) => {
    const { formatMessage, formatNumber } = useIntl();
    const { servicePlanItemId = '' } = useParams();

    const permissionManageServicePlans = useHasPermissions([PERMISSION.MANAGE_SERVICE_PLANS]);

    const [dateNow] = React.useState(() => Date.now());

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'version_number', desc: false }],
    });

    const {
        data: servicePlanPricingPolicies,
        isLoading,
        isError,
    } = useListServicePlanPricingPolicies({
        servicePlanItemId,
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
        config: { enabled: !!servicePlanItemId },
    });

    const columnHelper = createColumnHelper<ServicePlanItemPricingPolicy>();

    const columns = [
        columnHelper.accessor('version_number', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(servicePlansMessages.version),
            },
            maxSize: 45,
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            cell: CellBadge,
            meta: {
                title: formatMessage(generalMessages.status),
                badgeOptions: BILLING_STATUS_BADGE_OPTIONS,
            },
        }),
        columnHelper.accessor('valid_from', {
            enableSorting: true,
            header: DataTableColumnHeader,
            cell: CellDate,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(generalMessages.validFrom),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('valid_to', {
            enableSorting: true,
            header: DataTableColumnHeader,
            cell: CellDate,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(generalMessages.validTo),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        // A policy is priced either by a fixed amount or as a percentage of a reference rate, so
        // this column renders whichever of the two the policy actually carries.
        columnHelper.accessor('amount', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const { amount, reference_rate, rate_multiplier, currency } = row.original;

                if (reference_rate && rate_multiplier != null) {
                    const percent = Number((Number(rate_multiplier) * 100).toFixed(4));
                    return <span className="whitespace-nowrap">{`${percent}% \u00d7 ${reference_rate.name}`}</span>;
                }

                if (amount == null) return '-';

                return formatNumber(amount / 100, {
                    style: 'currency',
                    currency,
                });
            },
            meta: {
                title: formatMessage(pricingPoliciesMessages.amount),
            },
        }),
        columnHelper.accessor('reference_rate.name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => row.original.reference_rate?.name ?? '-',
            meta: {
                title: formatMessage(pricingPoliciesMessages.referenceRate),
            },
        }),
        columnHelper.accessor('currency', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return row.original.currency ? <CellCurrencyBadge currency={row.original.currency} /> : '-';
            },
            meta: {
                title: formatMessage(commonMessages.currency),
            },
        }),
        columnHelper.accessor('payment_type', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(pricingPoliciesMessages.paymentType),
                badgeOptions: PAYMENT_TYPE_BADGE_OPTIONS,
            },
        }),
        columnHelper.accessor('pricing_model', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(pricingPoliciesMessages.pricingModel),
                badgeOptions: PRICING_MODEL_BADGE_OPTIONS,
            },
        }),
        columnHelper.accessor('tax_reason.name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(pricingPoliciesMessages.taxReason),
            },
        }),
        ...(permissionManageServicePlans
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: ({ row }) => {
                          const allowed = !isServicePlanItemActive || row.original.status === BILLING_STATUS.SCHEDULED;
                          return allowed ? <CellServicePlanPricingPolicyActions row={row} /> : undefined;
                      },
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<ServicePlanItemPricingPolicy, unknown>
            columns={columns}
            data={servicePlanPricingPolicies}
            semantics={{
                title: formatMessage(pricingPoliciesMessages.pricingPolicies),
                entryName: formatMessage(pricingPoliciesMessages.policy).toLowerCase(),
                entryNames: formatMessage(pricingPoliciesMessages.policies).toLowerCase(),
                placeholder: formatMessage(pricingPoliciesMessages.policy).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            filters={filters}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageServicePlans && { newEntry: CreateServicePlanPricingPolicyFormModal }),
            }}
        />
    );
};

export default ServicePlanPricingPoliciesTable;
